import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Web token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

const formatCreatedDate = (created) => {
  if (!created) return 'Unknown';
  const dateString = typeof created === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(created)
    ? `${created}Z`
    : created;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString();
};

export default function UserDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialUser = location.state?.user;
  const userId = initialUser?.id;
  
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null);
  
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [permissions, setPermissions] = useState({
    job_perm: initialUser?.job_perm || '',
    invoice_perm: initialUser?.invoice_perm || '',
    price_perm: initialUser?.price_perm || '',
    quote_perm: initialUser?.quote_perm || '',
    customer_perm: initialUser?.customer_perm || '',
    view_all_jobs: initialUser?.view_all_jobs || false,
    edit_all_jobs: initialUser?.edit_all_jobs || false,
    delete_all_jobs: initialUser?.delete_all_jobs || false
  });
  const [basicInfo, setBasicInfo] = useState({
    firstName: initialUser?.firstName || '',
    lastName: initialUser?.lastName || '',
    email: initialUser?.email || '',
    phone: initialUser?.phone || '',
    user_type: initialUser?.user_type || initialUser?.userType || ''
  });

  // Fetch fresh user data from backend
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const token = getToken();
        if (!token) {
          alert('Please log in again.');
          navigate('/login');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userData = response.data;
        
        // DEBUG: Log user data
        console.log('User data from backend:', userData);
        console.log('user_type:', userData.user_type);
        console.log('userType:', userData.userType);
        
        setUser(userData);
        
        // Update all state with fresh data
        setPermissions({
          job_perm: userData.job_perm || '',
          invoice_perm: userData.invoice_perm || '',
          price_perm: userData.price_perm || '',
          quote_perm: userData.quote_perm || '',
          customer_perm: userData.customer_perm || '',
          view_all_jobs: userData.view_all_jobs || false,
          edit_all_jobs: userData.edit_all_jobs || false,
          delete_all_jobs: userData.delete_all_jobs || false
        });

        setBasicInfo({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          user_type: userData.user_type || ''
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.response?.status === 401) {
          alert('Your session has expired. Please log in again.');
          localStorage.removeItem('userToken');
          navigate('/login');
        } else if (error.response?.status === 403) {
          alert('You do not have permission to view this user.');
          navigate('/users');
        } else {
          alert('Failed to load user data.');
          navigate('/users');
        }
      }
    };

    fetchUserData();
  }, [userId, navigate]);

  // Check permissions on mount
  useEffect(() => {
    const userPermissionsString = localStorage.getItem('userPermissions');
    let currentUserType = null;
    
    if (userPermissionsString) {
      const userPermissions = JSON.parse(userPermissionsString);
      currentUserType = userPermissions.user_type;
      setUserType(currentUserType);
    }
    
    // Check permissions AFTER parsing
    if (currentUserType !== 'owner' && currentUserType !== 'admin') {
      alert('You do not have permission to view this page.');
      navigate('/');
    }
  }, [navigate]);

  const handlePermissionChange = (permType, value) => {
    setPermissions(prev => ({
      ...prev,
      [permType]: value
    }));
  };

  const handleSavePermissions = async () => {
    try {
      const token = getToken();
      if (!token) {
        alert('Please log in again.');
        navigate('/login');
        return;
      }

      const updateData = {
        id: user.id,
        job_perm: permissions.job_perm,
        invoice_perm: permissions.invoice_perm,
        price_perm: permissions.price_perm,
        quote_perm: permissions.quote_perm,
        customer_perm: permissions.customer_perm,
        view_all_jobs: permissions.view_all_jobs ? 1 : 0,
        edit_all_jobs: permissions.edit_all_jobs ? 1 : 0,
        delete_all_jobs: permissions.delete_all_jobs ? 1 : 0
      };

      await axios.put(`${API_BASE_URL}/api/users/edit/`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Silently reload the page
      window.location.reload();
    } catch (error) {
      console.error('Error saving permissions:', error);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('userToken');
        navigate('/login');
      } else if (error.response?.status === 403) {
        alert('You are not authorized to edit this user.');
      } else {
        alert('Failed to save permissions. Please try again.');
      }
    }
  };

  const handleCancelEdit = () => {
    // Reset permissions to original values
    setPermissions({
      job_perm: user?.job_perm || '',
      invoice_perm: user?.invoice_perm || '',
      price_perm: user?.price_perm || '',
      quote_perm: user?.quote_perm || '',
      customer_perm: user?.customer_perm || '',
      view_all_jobs: user?.view_all_jobs || false,
      edit_all_jobs: user?.edit_all_jobs || false,
      delete_all_jobs: user?.delete_all_jobs || false
    });
    setIsEditingPermissions(false);
  };

  const handleBasicInfoChange = (field, value) => {
    setBasicInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBasicInfo = async () => {
    try {
      const token = getToken();
      if (!token) {
        alert('Please log in again.');
        navigate('/login');
        return;
      }

      const updateData = {
        id: user.id,
        firstName: basicInfo.firstName,
        lastName: basicInfo.lastName,
        phone: basicInfo.phone,
        user_type: basicInfo.user_type
      };

      await axios.put(`${API_BASE_URL}/api/users/edit/`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update the user object with new basic info
      Object.assign(user, basicInfo);
      
      // If user type changed, reload the page to properly update permissions display
      if (user.user_type !== basicInfo.user_type || user.userType !== basicInfo.user_type) {
        window.location.reload();
      } else {
        setIsEditingBasicInfo(false);
      }
    } catch (error) {
      console.error('Error saving basic info:', error);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('userToken');
        navigate('/login');
      } else if (error.response?.status === 403) {
        alert('You are not authorized to edit this user.');
      } else {
        alert('Failed to save basic information. Please try again.');
      }
    }
  };

  const handleCancelBasicInfoEdit = () => {
    // Reset basic info to original values
    setBasicInfo({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      user_type: user?.user_type || user?.userType || ''
    });
    setIsEditingBasicInfo(false);
  };

  const handleDeleteUser = () => {
    if (window.confirm(`Are you sure you want to delete ${basicInfo.firstName} ${basicInfo.lastName}? This action cannot be undone.`)) {
      deleteUser();
    }
  };

  const deleteUser = async () => {
    try {
      const token = getToken();
      if (!token) {
        window.alert('Please log in again.');
        navigate('/login');
        return;
      }
  
      const response = await axios.delete(`${API_BASE_URL}/api/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      // Check if user deleted themselves
      if (response.data.self_delete) {
        // Clear storage and redirect to login
        localStorage.removeItem('userToken');
        localStorage.removeItem('userPermissions');
        window.alert('Your account has been deleted');
        navigate('/login');
      } else {
        // Just go back to users list
        window.alert('User deleted successfully');
        navigate('/users');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response?.status === 403) {
        window.alert(error.response.data.detail || 'You do not have permission to delete this user');
      } else if (error.response?.status === 404) {
        window.alert('User not found');
      } else {
        window.alert('Failed to delete user. Please try again.');
      }
    }
  };

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2>No User Data</h2>
          <p>No user information was provided.</p>
          <button style={styles.backButton} onClick={() => navigate('/users')}>
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>User Details</h1>
        <button style={styles.backButton} onClick={() => navigate('/users')}>
          Back to Users
        </button>
      </div>
      
      <div style={styles.content}>
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3>Basic Information</h3>
            {!isEditingBasicInfo ? (
              <button 
                style={styles.editButton} 
                onClick={() => setIsEditingBasicInfo(true)}
                title="Edit Basic Information"
              >
                ✏️
              </button>
            ) : (
              <div style={styles.editActions}>
                <button style={styles.saveButton} onClick={handleSaveBasicInfo}>
                  Save
                </button>
                <button style={styles.cancelButton} onClick={handleCancelBasicInfoEdit}>
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {!isEditingBasicInfo ? (
            <>
              <div style={styles.field}>
                <strong>Name:</strong> {user.firstName} {user.lastName}
              </div>
              <div style={styles.field}>
                <strong>Email:</strong> {user.email}
              </div>
              <div style={styles.field}>
                <strong>Phone:</strong> {user.phone || 'Not provided'}
              </div>
              <div style={styles.field}>
                <strong>User Type:</strong> {capitalizeFirstLetter(user.user_type)}
              </div>
            </>
          ) : (
            <>
              <div style={styles.field}>
                <strong>First Name:</strong>
                <input 
                  style={styles.inputField}
                  type="text"
                  value={basicInfo.firstName}
                  onChange={(e) => handleBasicInfoChange('firstName', e.target.value)}
                  placeholder="First Name"
                />
              </div>
              <div style={styles.field}>
                <strong>Last Name:</strong>
                <input 
                  style={styles.inputField}
                  type="text"
                  value={basicInfo.lastName}
                  onChange={(e) => handleBasicInfoChange('lastName', e.target.value)}
                  placeholder="Last Name"
                />
              </div>
              <div style={styles.field}>
                <strong>Email:</strong> {user.email}
              </div>
              <div style={styles.field}>
                <strong>Phone:</strong>
                <input 
                  style={styles.inputField}
                  type="tel"
                  value={basicInfo.phone}
                  onChange={(e) => handleBasicInfoChange('phone', e.target.value)}
                  placeholder="Phone"
                />
              </div>
              <div style={styles.field}>
                <strong>User Type:</strong>
                {(user.userType || user.user_type)?.toLowerCase() === 'owner' ? (
                  <span style={{ marginLeft: '10px', fontSize: '14px' }}>
                    {capitalizeFirstLetter(user.userType || user.user_type)}
                  </span>
                ) : (
                  <select 
                    style={styles.dropdown}
                    value={basicInfo.user_type}
                    onChange={(e) => handleBasicInfoChange('user_type', e.target.value)}
                  >
                    <option value="admin">Admin</option>
                    <option value="secondary">Secondary</option>
                  </select>
                )}
              </div>
            </>
          )}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3>Permissions</h3>
            {/* Hide edit button if user is admin or owner */}
            {(user.user_type || user.userType)?.toLowerCase() !== 'admin' && 
             (user.user_type || user.userType)?.toLowerCase() !== 'owner' && 
             basicInfo.user_type?.toLowerCase() !== 'admin' && 
             basicInfo.user_type?.toLowerCase() !== 'owner' && (
              !isEditingPermissions ? (
                <button 
                  style={styles.editButton} 
                  onClick={() => setIsEditingPermissions(true)}
                  title="Edit Permissions"
                >
                  ✏️
                </button>
              ) : (
                <div style={styles.editActions}>
                  <button style={styles.saveButton} onClick={handleSavePermissions}>
                    Save
                  </button>
                  <button style={styles.cancelButton} onClick={handleCancelEdit}>
                    Cancel
                  </button>
                </div>
              )
            )}
          </div>
          
          {!isEditingPermissions ? (
            <>
              {((user.user_type || user.userType)?.toLowerCase() === 'admin' || 
                (user.user_type || user.userType)?.toLowerCase() === 'owner' || 
                basicInfo.user_type?.toLowerCase() === 'admin' || 
                basicInfo.user_type?.toLowerCase() === 'owner') ? (
                <div style={styles.field}>
                  <p>
                    {basicInfo.user_type?.toLowerCase() === 'owner' || (user.user_type || user.userType)?.toLowerCase() === 'owner'
                      ? 'Owners have full read/write/edit/delete permissions for all customers, jobs, invoices, and quotes.'
                      : 'Admins have full read/write/edit/delete permissions for all customers, jobs, invoices, and quotes.'}
                  </p>
                </div>
              ) : (
                <>
                  <div style={styles.field}>
                    <strong>Job Permissions:</strong> {capitalizeFirstLetter(user.job_perm) || 'None'}
                  </div>
                  <div style={styles.field}>
                    <strong>View All Jobs:</strong> {user.view_all_jobs ? 'Yes' : 'No'}
                  </div>
                  <div style={styles.field}>
                    <strong>Edit All Jobs:</strong> {user.edit_all_jobs ? 'Yes' : 'No'}
                  </div>
                  <div style={styles.field}>
                    <strong>Delete All Jobs:</strong> {user.delete_all_jobs ? 'Yes' : 'No'}
                  </div>
                  <div style={styles.field}>
                    <strong>Invoice Permissions:</strong> {capitalizeFirstLetter(user.invoice_perm) || 'None'}
                  </div>
                  <div style={styles.field}>
                    <strong>Price Permissions:</strong> {capitalizeFirstLetter(user.price_perm) || 'None'}
                  </div>
                  <div style={styles.field}>
                    <strong>Quote Permissions:</strong> {capitalizeFirstLetter(user.quote_perm) || 'None'}
                  </div>
                  <div style={styles.field}>
                    <strong>Customer Permissions:</strong> {capitalizeFirstLetter(user.customer_perm) || 'None'}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {(basicInfo.user_type?.toLowerCase() === 'admin' || basicInfo.user_type?.toLowerCase() === 'owner') ? (
                <div style={styles.field}>
                  <p>
                    {basicInfo.user_type?.toLowerCase() === 'owner'
                      ? 'Owners have full read/write/edit/delete permissions for all customers, jobs, invoices, and quotes.'
                      : 'Admins have full read/write/edit/delete permissions for all customers, jobs, invoices, and quotes.'}
                  </p>
                </div>
              ) : (
                <>
                  <div style={styles.field}>
                    <strong>Job Permissions:</strong>
                    <select 
                      style={styles.dropdown}
                      value={permissions.job_perm}
                      onChange={(e) => handlePermissionChange('job_perm', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="read">Read</option>
                      <option value="write">Write</option>
                    </select>
                  </div>
                  <div style={styles.field}>
                    <strong>View All Jobs:</strong>
                    <input 
                      type="checkbox"
                      checked={permissions.view_all_jobs}
                      onChange={(e) => handlePermissionChange('view_all_jobs', e.target.checked)}
                      style={{ marginLeft: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </div>
                  <div style={styles.field}>
                    <strong>Edit All Jobs:</strong>
                    <input 
                      type="checkbox"
                      checked={permissions.edit_all_jobs}
                      onChange={(e) => handlePermissionChange('edit_all_jobs', e.target.checked)}
                      style={{ marginLeft: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </div>
                  <div style={styles.field}>
                    <strong>Delete All Jobs:</strong>
                    <input 
                      type="checkbox"
                      checked={permissions.delete_all_jobs}
                      onChange={(e) => handlePermissionChange('delete_all_jobs', e.target.checked)}
                      style={{ marginLeft: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </div>
                  <div style={styles.field}>
                    <strong>Invoice Permissions:</strong>
                    <select 
                      style={styles.dropdown}
                      value={permissions.invoice_perm}
                      onChange={(e) => handlePermissionChange('invoice_perm', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="read">Read</option>
                      <option value="write">Write</option>
                    </select>
                  </div>
                  <div style={styles.field}>
                    <strong>Price Permissions:</strong>
                    <select 
                      style={styles.dropdown}
                      value={permissions.price_perm}
                      onChange={(e) => handlePermissionChange('price_perm', e.target.value)}
                    >
                      <option value="visible">Visible</option>
                      <option value="hidden">Hidden</option>
                    </select>
                  </div>
                  <div style={styles.field}>
                    <strong>Quote Permissions:</strong>
                    <select 
                      style={styles.dropdown}
                      value={permissions.quote_perm}
                      onChange={(e) => handlePermissionChange('quote_perm', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="read">Read</option>
                      <option value="write">Write</option>
                    </select>
                  </div>
                  <div style={styles.field}>
                    <strong>Customer Permissions:</strong>
                    <select 
                      style={styles.dropdown}
                      value={permissions.customer_perm}
                      onChange={(e) => handlePermissionChange('customer_perm', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="read">Read</option>
                      <option value="write">Write</option>
                    </select>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div style={styles.section}>
          <h3>Account Status</h3>
          <div style={styles.field}>
            <strong>Confirmed:</strong> {user.user_confirmed ? 'Yes' : 'No'}
          </div>
          <div style={styles.field}>
            <strong>Created:</strong> {formatCreatedDate(user.created)}
          </div>
          <div style={styles.field}>
            <strong>Last Edited:</strong> {(() => {
              if (!user.edited) return 'Never';
              const editedDate = new Date(user.edited);
              // Check if date is 1/1/1970 (Unix epoch)
              if (editedDate.getFullYear() === 1970 && editedDate.getMonth() === 0 && editedDate.getDate() === 1) {
                return 'N/A';
              }
              return editedDate.toLocaleDateString();
            })()}
          </div>
        </div>

        {/* Delete User Button */}
        <button style={styles.deleteUserButton} onClick={handleDeleteUser}>
          Delete User
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  backButton: {
    backgroundColor: '#007BFF',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  section: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  field: {
    marginBottom: '10px',
    fontSize: '16px',
    lineHeight: '1.5',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '50px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  editButton: {
    backgroundColor: '#007BFF',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editActions: {
    display: 'flex',
    gap: '10px',
  },
  saveButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  dropdown: {
    marginLeft: '10px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
    minWidth: '100px',
  },
  inputField: {
    marginLeft: '10px',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
    minWidth: '200px',
    outline: 'none',
  },
  deleteUserButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    marginTop: '20px',
  },
};

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};